import React, { useEffect, useState } from "react";
import { AlertTriangle, Download, Archive } from "lucide-react";
import api from "../lib/api";

function PlanTable({ counts }) {
  const rows = Object.entries(counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs" data-testid="prelaunch-counts">
      {rows.length === 0 ? <span className="text-[#9E978E]">Nothing to remove.</span> : rows.map(([c, n]) => (
        <div key={c} className="flex justify-between border-b border-white/5 py-0.5"><span className="text-[#B5AEA4]">{c}</span><span className="text-[#FBF8F3] font-mono">{n}</span></div>))}
    </div>
  );
}

export default function AdminPrelaunchReset({ run }) {
  const [p, setP] = useState(null);
  const [exp, setExp] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);
  const load = () => api.get("/admin/prelaunch-reset/plan").then((r) => { setP(r.data); setExp(r.data.last_export && !r.data.last_export.executed_at ? r.data.last_export : null); });
  useEffect(() => { load(); }, []);
  if (!p) return <div className="text-sm text-[#9E978E]">Loading reset plan…</div>;

  const doExport = () => run(async () => { const r = await api.post("/admin/prelaunch-reset/export"); setExp(r.data); await load(); }, "Archive created");
  // Blob errors are parsed so the shared step-up handler can prompt for a code and retry.
  const download = () => run(async () => {
    try {
      const res = await api.get(`/admin/prelaunch-reset/exports/${exp.export_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `datebid_${exp.export_id}.json.gz`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try { e.response.data = JSON.parse(await e.response.data.text()); } catch (_) { e.response.data = {}; }
      }
      throw e;
    }
  }, "Archive downloaded");
  const fresh = !!exp && p.last_export_fresh && p.last_export?.export_id === exp.export_id;
  const canRun = p.execute_enabled && p.blockers.length === 0 && fresh && phrase.trim() === p.confirm_phrase && reason.trim().length >= 10;
  const doExecute = () => run(async () => {
    const r = await api.post("/admin/prelaunch-reset/execute", { export_id: exp.export_id, plan_hash: p.plan_hash, confirm: phrase.trim(), reason: reason.trim() });
    setResult(r.data); setPhrase(""); await load();
  }, "Pre-launch reset finished");

  const clearLock = () => {
    const why = window.prompt("Reason for letting members back in (min 10 characters)");
    if (!why || why.trim().length < 10) return;
    run(async () => { await api.post("/admin/prelaunch-reset/clear-lock", { reason: why.trim() }); await load(); }, "Members can sign in again");
  };

  return (
    <div className="rounded-2xl border border-[#8B263E]/40 bg-[#141113]/70 p-6 space-y-4" data-testid="prelaunch-reset-card">
      <div className="flex items-center gap-2">
        <AlertTriangle size={18} className="text-[#E58CA0]" />
        <h3 className="font-serif text-lg text-[#FBF8F3]">Pre-launch Reset</h3>
        <span className="ml-auto text-xs uppercase tracking-wide text-[#C9A96E]" data-testid="prelaunch-env">{p.environment}</span>
      </div>
      <p className="text-sm text-[#9E978E]">
        Signs out and pauses all members while it runs, takes a final archive, then removes <span className="text-[#D8D2C9]">every member account</span> and their profiles, photos, ID documents, offers, messages,
        credits and test payments. Keeps the admin account, settings, markets, promo codes and the full audit log.
        An archive is required first, and the reset needs a fresh verification code and the typed confirmation phrase.
      </p>
      <div className="rounded-xl border border-white/8 bg-[#0C0A0A]/50 p-4 space-y-2">
        <p className="text-xs uppercase tracking-wide text-[#C9A96E]">Dry run: what would be removed · {p.users.members} member account(s) · {p.files_to_destroy} stored file(s)</p>
        <PlanTable counts={p.counts} />
        <p className="text-xs text-[#9E978E]" data-testid="prelaunch-keep">Kept: {p.keep.join(", ")}</p>
      </div>
      {p.warnings?.length > 0 && (
        <ul className="text-sm text-[#E58CA0] list-disc pl-5" data-testid="prelaunch-warnings">{p.warnings.map((w) => <li key={w}>{w}</li>)}</ul>)}
      {p.lock_active && (
        <button data-testid="prelaunch-clear-lock-btn" onClick={clearLock} className="rounded-full border border-[#E58CA0]/50 text-[#E58CA0] px-4 py-2 text-sm">Let members sign in again</button>)}
      {p.blockers.length > 0 && (
        <ul className="text-sm text-[#E5A84B] list-disc pl-5" data-testid="prelaunch-blockers">{p.blockers.map((b) => <li key={b}>{b}</li>)}</ul>)}
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="prelaunch-refresh-btn" onClick={load} className="rounded-full border border-[#D4AF37]/30 text-[#C9A96E] px-4 py-2 text-sm">Refresh plan</button>
        <button data-testid="prelaunch-export-btn" onClick={doExport} disabled={!!fresh} title={fresh ? "A current archive already exists" : undefined} className="rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-40"><Archive size={14} /> Step 1: Create archive</button>
        {exp && <button data-testid="prelaunch-download-btn" onClick={download} className="rounded-full border border-white/15 text-[#D8D2C9] px-4 py-2 text-sm flex items-center gap-1.5"><Download size={14} /> Download archive</button>}
      </div>
      {exp && (
        <p className={`text-xs ${fresh ? "text-[#9E978E]" : "text-[#E5A84B]"}`} data-testid="prelaunch-export-info">
          Archive {exp.export_id} · {Math.round(exp.size / 1024)} KB · SHA-256 {exp.sha256.slice(0, 16)}… · {exp.created_at?.slice(0, 19)} UTC
          {!fresh && " · out of date (data changed or older than 2 hours): create a new one"}
        </p>)}
      {!p.execute_enabled ? (
        <p className="text-sm text-[#9E978E]" data-testid="prelaunch-disabled">Step 2 is switched off in this environment. The reset cannot run here.</p>
      ) : (
        <div className="space-y-2" data-testid="prelaunch-execute-form">
          <input data-testid="prelaunch-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (min 10 characters)"
            className="w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3]" />
          <input data-testid="prelaunch-phrase-input" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={`Type: ${p.confirm_phrase}`}
            className="w-full rounded-xl bg-[#0C0A0A]/60 border border-[#8B263E]/40 px-3 py-2 text-sm text-[#FBF8F3] font-mono" />
          <button data-testid="prelaunch-execute-btn" disabled={!canRun} onClick={doExecute}
            className="rounded-full bg-[#8B263E] text-[#FBF8F3] px-5 py-2 text-sm font-medium disabled:opacity-40">Step 2: Run pre-launch reset</button>
        </div>)}
      {result && <pre className="text-xs text-[#D8D2C9] bg-black/30 rounded-xl p-3 overflow-auto" data-testid="prelaunch-result">{JSON.stringify(result, null, 2)}</pre>}
    </div>
  );
}
